import BN from 'bignumber.js';

// Constants
const DEFAULT_DECIMAL_PLACES = 4;
const SIGNIFICANT_DIGITS = 6;
const CURRENCY_UNITS: ('K' | 'M' | 'B' | 'T')[] = ['K', 'M', 'B', 'T'];
const CURRENCY_UNIT_NUM = 10 ** 3; // 1000

// Decimal short form symbols
const decimalShortFormSymbolMap: Record<number, string> = {
  1: '₁',
  2: '₂',
  3: '₃',
  4: '₄',
  5: '₅',
  6: '₆',
  7: '₇',
  8: '₈',
  9: '₉',
  10: '₁₀',
  11: '₁₁',
  12: '₁₂',
  13: '₁₃',
  14: '₁₄',
  15: '₁₅',
  16: '₁₆',
  17: '₁₇',
  18: '₁₈',
  19: '₁₉',
  20: '₂₀',
};

const minExclusiveNumToShowShortDecimal = 0.0001;

export type BigNumberish = string | number;

/**
 * Convert value to BigNumber
 */
export function toBN(value: BigNumberish | BN): BN {
  let number = value;
  if (typeof number === 'undefined') {
    number = 0;
  }
  return new BN(number.toString(10), 10);
}

/**
 * Add thousand separators to number
 */
const toCurrencyNumber = (x: number | string): string => {
  const parts = x.toString().split('.');
  if (parts.length > 0) {
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }
  return '';
};

/**
 * Count trailing zeros after decimal point
 */
export function countNumTrailingZeros(num: BigNumberish | BN): number {
  const numBN = toBN(num);
  const numStr = numBN.toString(10);
  const decimalPart = numStr.split('.')[1];
  if (decimalPart) {
    let countZeros = 0;
    for (let i = 0; i < decimalPart.length; i++) {
      if (decimalPart[i] === '0') {
        countZeros++;
      } else {
        break;
      }
    }
    return countZeros;
  } else {
    return 0;
  }
}

/**
 * Get short form decimal with subscript symbols
 */
export function getShortFormDeciamal(numBN: BigNumberish | BN, tailSignificantDigits = 4): string {
  const maxCountZeros = 20;
  const numStr = numBN.toString(10);
  const countZeros = countNumTrailingZeros(numBN);
  const [integerPart, decimalPart] = numStr.split('.');
  if (countZeros > maxCountZeros || countZeros === 0) {
    return numStr;
  }

  const tail = decimalPart.slice(countZeros, decimalPart.length).slice(0, tailSignificantDigits);
  const tailWithPaddingZero = `${tail}${'0'.repeat(tailSignificantDigits - tail.length)}`;
  return `${integerPart}.0${decimalShortFormSymbolMap[countZeros]}${tailWithPaddingZero}`;
}

/**
 * Format compact number with 4 significant digits (e.g., 1234567890 => 1.235B)
 */
function formatCompactWith4Digits(number: number) {
  const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumSignificantDigits: 4,
  });

  return formatter.format(number);
}

/**
 * Convert large numbers to currency format with K/M/B/T units
 */
export function getBigCurrencyNum(
  numBN: BN,
  unit: 'K' | 'M' | 'B' | 'T',
  roundingMode?: BN.RoundingMode,
  minDecimalPlaces = DEFAULT_DECIMAL_PLACES
): { numBN: BN; numStr: string; minDecimalPlaces?: number } {
  if (!roundingMode) roundingMode = BN.ROUND_DOWN;
  const i = CURRENCY_UNITS.findIndex(u => u === unit);
  let numStr = numBN.toString(10);
  numBN = numBN.div(CURRENCY_UNIT_NUM);
  // get the biggest unit
  if (numBN.gte(CURRENCY_UNIT_NUM) && unit !== 'T') {
    return getBigCurrencyNum(numBN, CURRENCY_UNITS[i + 1], roundingMode, minDecimalPlaces);
  }
  if (unit === 'T' && numBN.gte(CURRENCY_UNIT_NUM)) {
    numBN = numBN.integerValue(roundingMode);
  } else if (minDecimalPlaces !== 0) {
    numBN = numBN.precision(minDecimalPlaces, roundingMode);
  }

  numStr = `${
    minDecimalPlaces === 0
      ? numBN.toFixed(0, roundingMode)
      : numBN.toPrecision(minDecimalPlaces, roundingMode)
  }${CURRENCY_UNITS[i]}`;

  return {
    numBN,
    numStr,
  };
}

/**
 * Format the given number for display with various options.
 *
 * @param {Object} options - The formatting options.
 * @param {BigNumberish | BN} options.num - The input number to be formatted.
 * @param {boolean} [options.isShowSeparator=true] - Whether to show separators in the formatted number.
 * @param {'price' | 'normal'} [options.type='normal'] - The type of formatting to apply.
 * @param {boolean} [options.isOperateNum=false] - Whether to the number is input which operate by user
 * @param {boolean} [options.isShowTBMK=false] - Whether to show TBMK unit for numbers greater than or equal to 10000.
 * @param {boolean} [options.showPositive] - Whether to show a positive sign for positive numbers.
 * @param {BN.RoundingMode} [options.roundingMode=BN.ROUND_DOWN] - The rounding mode to be used for formatting.
 * @param {number} [options.significantDigits=SIGNIFICANT_DIGITS] - The number of significant digits to display in the formatted number.
 * @param {number} [options.minDecimalPlaces=DEFAULT_DECIMAL_PLACES] - The minimum number of decimal places to display in the formatted number.
 * @param {boolean} [options.isShortFormDecimal=false] - Whether to use short form decimal notation.
 * @returns {string} The formatted number as a string.
 */
export function formatDisplayNumber({
  num,
  isShowSeparator = true,
  type = 'normal',
  isOperateNum = false,
  isShowTBMK = false,
  isShowApproximatelyEqualTo = true,
  showPositive,
  roundingMode = BN.ROUND_DOWN,
  significantDigits = SIGNIFICANT_DIGITS,
  minDecimalPlaces = DEFAULT_DECIMAL_PLACES,
  showTrailingZeros = true,
}: {
  num: BigNumberish | BN;
  isShowSeparator?: boolean;
  type?: 'price' | 'normal';
  isOperateNum?: boolean;
  isShowTBMK?: boolean;
  showPositive?: boolean;
  isShowApproximatelyEqualTo?: boolean;
  roundingMode?: BN.RoundingMode;
  significantDigits?: number;
  minDecimalPlaces?: number;
  showTrailingZeros?: boolean;
}): string {
  let numBN = toBN(num);
  if (numBN.isNaN() || numBN.eq(Infinity)) {
    numBN = toBN(0);
  }
  let sign = '';
  if (numBN.isNegative()) {
    sign = '-';
    numBN = numBN.abs();
  } else if (showPositive && numBN.gt(0)) {
    sign = '+';
  }
  let numStr = numBN.toString(10);

  if (isOperateNum) {
    if (numBN.gte(1)) {
      numStr = numBN.toFixed(minDecimalPlaces, roundingMode);
    } else {
      numStr = numBN.toPrecision(minDecimalPlaces, roundingMode);
    }
  } else {
    // >=1000 use TBMK unit
    if (numBN.eq(0)) {
      numStr = '0';
    } else if (numBN.gte(10000)) {
      const numRes = numBN.integerValue(roundingMode);
      numStr = numRes.toString();
      if (isShowTBMK) {
        numStr = formatCompactWith4Digits(Number(numBN.toNumber()));
      }
      if (numStr.includes('e+')) {
        numStr = Number(numStr).toExponential(4);
      }
    } else if (numBN.gte(1000) && numBN.lt(10000)) {
      if (type === 'price') {
        numBN = numBN.precision(significantDigits, roundingMode);
        numStr = numBN.toPrecision(significantDigits, roundingMode);
      } else {
        const numRes = numBN.precision(significantDigits, roundingMode);
        numStr = numRes.toPrecision(significantDigits, roundingMode);
        if (isShowTBMK) {
          numStr = formatCompactWith4Digits(Number(numRes.toNumber()));
        }
      }
    } else if (numBN.gte(1) && numBN.lt(1000)) {
      numBN = numBN.precision(significantDigits, roundingMode);
      if (minDecimalPlaces === 0) {
        numStr = numBN.toFixed(0, roundingMode);
      } else {
        numStr = numBN.toPrecision(significantDigits, roundingMode);
      }
    } else if (numBN.gte(0.0001) && numBN.lt(1)) {
      if (type === 'price') {
        if (
          showTrailingZeros &&
          numBN.lt(BN(minExclusiveNumToShowShortDecimal)) &&
          numBN.gt(BN(0))
        ) {
          numBN = numBN.precision(significantDigits, roundingMode);
          numStr = getShortFormDeciamal(numBN);
        } else {
          numBN = numBN.precision(significantDigits, roundingMode);
          numStr = numBN.toPrecision(significantDigits, roundingMode);
        }
      } else {
        numStr = numBN.toFixed(minDecimalPlaces, roundingMode);
      }
    } else {
      if (type === 'price') {
        numBN = numBN.precision(significantDigits - 1, roundingMode);
        const decimalPlaces = numBN.decimalPlaces() || 0;
        if (
          showTrailingZeros &&
          numBN.lt(BN(minExclusiveNumToShowShortDecimal)) &&
          numBN.gt(BN(0))
        ) {
          numStr = getShortFormDeciamal(numBN);
        } else {
          if (decimalPlaces > 8) {
            numStr = numBN.toFixed(8, roundingMode);
          } else {
            numStr = numBN.toPrecision(4, roundingMode);
          }
        }
      } else {
        // show `<0.0001` when 0< num < 0.0001
        numStr = numBN.toFixed(minDecimalPlaces, roundingMode);
        return `${isShowApproximatelyEqualTo ? '≈' : ''}${numStr}`;
      }
    }
  }

  if (isShowSeparator) {
    numStr = toCurrencyNumber(numStr);
  }

  return `${sign}${numStr}`;
}

/**
 * Format number with specified decimal places
 */
export function formatNumber(
  num: BigNumberish | BN,
  decimals: number = DEFAULT_DECIMAL_PLACES,
  isShowSeparator = true,
  roundingMode: BN.RoundingMode = BN.ROUND_DOWN,
  isRemoveTrailingZeros = false
): string {
  let numBN = toBN(num);
  if (numBN.isNaN() || numBN.eq(Infinity)) {
    numBN = toBN(0);
  }

  let res = numBN.toFixed(decimals, roundingMode);

  if (res.includes('e+')) {
    res = Number(res).toExponential(4);
  }

  if (isShowSeparator) {
    res = toCurrencyNumber(res);
  }
  if (isRemoveTrailingZeros) {
    res = removeTrailingZeros(res);
  }

  return res;
}

/**
 * Remove trailing zeros after decimal point
 */
export function removeTrailingZeros(value: string | number): string {
  let str = value.toString();
  if (str.match(/\./)) {
    str = str.replace(/\.?0+$/, '');
  }
  return str;
}

/**
 * Format currency with K/M/B suffix for chart axis
 */
export function formatCurrencyCompact(value: BigNumberish): string {
  return `$${formatDisplayNumber({ num: value, isShowTBMK: true, minDecimalPlaces: 2 })}`;
}

/**
 * Format currency for Y-axis ticks with smart decimal handling
 * - Uses K/M/B suffix
 * - No decimals for clean numbers (e.g., $500K, $1M)
 * - Shows decimals only when needed (e.g., $1.5M)
 */
export function formatCurrencyAxis(value: BigNumberish): string {
  const num = Number(toBN(value).toNumber());
  if (isNaN(num) || !isFinite(num)) {
    return '$0';
  }

  // Format with compact notation
  const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

  const formatted = formatter.format(num);

  // Remove unnecessary .0 suffix (e.g., "1.0K" -> "1K")
  const cleaned = formatted.replace(/\.0([KMBT]?)$/, '$1');

  return `$${cleaned}`;
}

/**
 * Format currency with K/M/B suffix with fixed decimal places
 * e.g., 1234567890 => $1.23B (always 2 decimal places)
 */
export function formatCurrencyCompactFixed(value: BigNumberish, decimals = 2): string {
  const num = Number(toBN(value).toNumber());
  if (isNaN(num) || !isFinite(num)) {
    return '$0.00';
  }
  const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `$${formatter.format(num)}`;
}

/**
 * Format currency with full precision for tooltip display
 * Removes trailing zeros (e.g., $1,234.00 -> $1,234)
 */
export function formatCurrencyFull(value: BigNumberish): string {
  const formatted = formatDisplayNumber({ num: value, minDecimalPlaces: 2 });
  return `$${removeTrailingZeros(formatted)}`;
}

/**
 * Format currency with fixed 2 decimal places and thousand separators
 * Always shows 2 decimal places (e.g., $1,234,567.00)
 */
export function formatCurrencyFullFixed(value: BigNumberish, decimals = 2): string {
  const num = Number(toBN(value).toNumber());
  if (isNaN(num) || !isFinite(num)) {
    return '$0.00';
  }
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `$${formatter.format(num)}`;
}

/**
 * Format integer number with thousand separators for chart axis
 */
export function formatIntegerCompact(value: BigNumberish): string {
  return formatDisplayNumber({ num: value, isShowTBMK: true, minDecimalPlaces: 0 });
}

/**
 * Format integer number with thousand separators for tooltip display
 * Always rounds to integer (no decimals)
 */
export function formatIntegerFull(value: BigNumberish): string {
  const num = Math.round(Number(toBN(value).toNumber()));
  if (isNaN(num) || !isFinite(num)) {
    return '0';
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}
